import { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import { useQuery } from '@tanstack/react-query';
import { useApr, useApy } from '@ui/hooks/useApr';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import useCoinPerStakedCoin from '@ui/hooks/useCoinPerStakedCoin';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import useLcroRedeemableAtVVSPool from '@ui/hooks/useLcroRedeemableAtVVSPool';
import { useLiquidCoinBalance } from '@ui/hooks/useLiquidCoinBalance';
import useLpUsdValue from '@ui/hooks/useLpUsdValue';
import useNftTokens from '@ui/hooks/useNftTokens';
import { useTectonicExchangeRate } from '@ui/hooks/usePartnerStats';
import { GraphQLContext } from '@ui/providers/GraphQLProvider';
import { getFerroAtomLpPrice, getFerroLpPrice } from '@ui/queries/history';
import aprToGrossApy from '@ui/utils/aprToGrossApy';
import { useContext } from 'react';

import useEarningsData from './useEarningsData';

const useDashboardStakingData = (includeLpEarnings?: boolean) => {
  const { data: coinPerStakedCoin, isLoading: croPerStcroLoading } =
    useCoinPerStakedCoin();
  const { data: liquidBalance, isLoading: liquidBalanceLoading } =
    useLiquidCoinBalance();
  const client = useContext(GraphQLContext).historyClient;
  const atomClient = useContext(GraphQLContext).historyClientAtom;
  const coinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[coinMode];

  const lpPriceQuery =
    coinMode === 'atom'
      ? getFerroAtomLpPrice(atomClient)
      : getFerroLpPrice(client);

  const { data: lpPriceInCro } = useQuery(lpPriceQuery);
  const {
    lifetimeEarnings,
    pendingEarnings,
    dates,
    isLoading: earningDataLoading,
    currentFerroFactor,
    currentFerroLpBalance,
    currentTLcroBalance,
    currentVvsCroLcroBalance,
  } = useEarningsData(!!includeLpEarnings, coinMode);
  const currentVvsCroLcroBalanceUsd = useLpUsdValue(
    'VVS_CRO',
    currentVvsCroLcroBalance ? `${currentVvsCroLcroBalance}` : '0',
  );

  const currentTLcroBalanceUsd = useLpUsdValue(
    'TECTONIC',
    currentTLcroBalance ? String(currentTLcroBalance) : '0',
  );

  const currentFerroLpBalanceUsd = useLpUsdValue(
    'FERRO',
    currentFerroLpBalance ? String(currentFerroLpBalance) : '0',
  );

  const { data: lcroRedeemableAtVvsBn } = useLcroRedeemableAtVVSPool(
    currentVvsCroLcroBalance,
  );
  const croOnVvs = coinPerStakedCoin
    ? lcroRedeemableAtVvsBn
        ?.mul(Math.round(coinPerStakedCoin * 1_000_000))
        .div(1_000_000)
    : undefined;

  const stakedCroAtFerro =
    currentFerroLpBalance === undefined ||
    currentFerroFactor === undefined ||
    lpPriceInCro === undefined
      ? undefined
      : currentFerroLpBalance * currentFerroFactor * lpPriceInCro;

  const precisionToCoin = Math.floor(coinModeInfo.decimals / 2);
  const stakedCroAtFerroBn =
    stakedCroAtFerro !== undefined
      ? BigNumber.from(
          Math.round(10 ** precisionToCoin * stakedCroAtFerro),
        ).mul(10 ** (coinModeInfo.decimals - precisionToCoin))
      : undefined;

  const tectonicExchangeRate = useTectonicExchangeRate();

  let lcroAtTectonic = BigNumber.from('0');

  try {
    if (tectonicExchangeRate?.data) {
      lcroAtTectonic = parseUnits(
        String(currentTLcroBalance / tectonicExchangeRate.data),
        18,
      );
    }
  } catch (e) {
    // do nothing
  }

  const croStaked =
    coinPerStakedCoin && stakedCroAtFerroBn
      ? liquidBalance
          ?.add(lcroAtTectonic)
          ?.mul(Math.round(coinPerStakedCoin * 1_000_000))
          .div(1_000_000)
          .add(stakedCroAtFerroBn)
          .add(croOnVvs ?? BigNumber.from(0))
      : undefined;

  const { data: stakedUsdValue, isLoading: stakedUsdValueLoading } =
    useCoinUsdValue(croStaked);

  const { data: lcroBalanceUsdValue, isLoading: lcroBalanceUsdValueLoading } =
    useCoinUsdValue(
      coinPerStakedCoin
        ? liquidBalance
            ?.mul(Math.round(coinPerStakedCoin * 1_000_000))
            .div(1_000_000)
        : undefined,
    );

  const {
    redeemableTokenAmount,
    pendingUsdAmount,
    pendingTokenAmount,
    redeemableUsdAmount,
    redeemableNftTokens,
  } = useNftTokens();

  const { data: apy, isLoading: apyLoading } = useApy();

  const lifeTimeEarningsCro = lifetimeEarnings.length
    ? lifetimeEarnings[lifetimeEarnings.length - 1]
    : undefined;
  const pendingEarningsCro = pendingEarnings.length
    ? pendingEarnings[pendingEarnings.length - 1]
    : undefined;

  const { data: lifeTimeEarningsUsd } = useCoinUsdValue(lifeTimeEarningsCro);
  const { data: pendingEarningsUsd } = useCoinUsdValue(pendingEarningsCro);

  const { data: apr } = useApr();
  const grossApy = aprToGrossApy(apr);

  return {
    liquidBalance,
    liquidBalanceLoading,
    lcroBalanceUsdValue,
    lcroBalanceUsdValueLoading,
    croStaked,
    croStakedLoading: croPerStcroLoading || liquidBalanceLoading,
    redeemableTokenAmount,
    pendingUsdAmount,
    pendingTokenAmount,
    redeemableUsdAmount,
    apy,
    apyLoading,
    stakedUsdValue,
    stakedUsdValueLoading,
    redeemableNftTokens,
    lifeTimeEarningsCro,
    pendingEarningsCro,
    lifeTimeEarningsUsd:
      earningDataLoading || lifetimeEarnings.length ? lifeTimeEarningsUsd : 0,
    pendingEarningsUsd:
      earningDataLoading || pendingEarnings.length ? pendingEarningsUsd : 0,
    lifetimeEarnings,
    pendingEarnings,
    dates,
    pendingEarningsLoading: earningDataLoading,
    lifeTimeEarningsLoading: earningDataLoading,
    grossApy,
    currentFerroLpBalance,
    currentFerroLpBalanceUsd,
    currentVvsCroLcroBalanceUsd,
    currentVvsCroLcroBalance,
    currentTLcroBalance,
    currentTLcroBalanceUsd,
  };
};

export default useDashboardStakingData;
